import { CheckGroupProps } from '../components/elements/form/CheckGroup'
import { TextAreaGroupProps } from '../components/elements/form/TextAreaGroup'
import { LocationConfirmStateProps, TrackingProps } from '../constants/map'
import {
  RA_LCAR_COST_SC_WISHES,
  RA_LODGING_SC_WISHES,
  RA_TRANSFER_SC_WISHES,
} from '../constants/qnState'
import { StatusProps } from '../utils/status'

import { QnFrontVersion, QnFrontVersionProps } from './versionControl'
import { VEHICLE_STATE_CONFIRM_TITLE } from '../constants/qnState'
import ContractInfo from 'components/pages/ContractInfo'

/**
 *  新ヘルプボタンの設置にて途中入力送信で正しい情報を送信するため
 *  レンダリングされるまで EditingItem の初期値を[""]にする必要がある。
 *  ローカルストレージの値を key として使用しているため、空文字の key を許容する
 */

/** はい・いいえの二択 */
export const YesNoSelection = {
  /** はい */
  yes: 'yes',
  /** いいえ */
  no: 'no',
} as const
export type YesNoSelection = keyof typeof YesNoSelection

/** 四輪自動車の箇所 */
export const FourWheeledVehiclePart = {
  /** 左まえ */
  leftFront: 'leftFront',
  /** 右まえ */
  rightFront: 'rightFront',
  /** 左うしろ */
  leftRear: 'leftRear',
  /** 右うしろ */
  rightRear: 'rightRear',
} as const
export const AllPart = {
  /** まえ */
  allFront: 'allFront',
  /** うしろ */
  allRear: 'allRear',
} as const
export type FourWheeledVehiclePart = keyof typeof FourWheeledVehiclePart
export type AllPart = keyof typeof AllPart
/** バイクの箇所 */
export const MotorbikePart = {
  /** まえ */
  front: 'front',
  /** うしろ */
  rear: 'rear',
} as const
export type MotorbikePart = keyof typeof MotorbikePart

/** 四輪・バイクの車体箇所 */
export type VehicleBodyPart = FourWheeledVehiclePart | MotorbikePart

/** 車体の選択可能箇所 */
export type VehiclePart = FourWheeledVehiclePart | AllPart | MotorbikePart

export const AccidentImageTypes = {
  peripheral: 'peripheral',
  damagedPart: 'damagedpart',
  vehicleCloseUp: 'vehicleCloseUp',
  landmark: 'landmark',
  carVerification: 'carVerification',
  supplement: 'supplement',
} as const
export type AccidentImageTypes = keyof typeof AccidentImageTypes

/** 事故状況の種類 */
export const AccidentStatusTypes = {
  /** 初期値 */
  '': '',
  /** 車と車の事故 */
  car: 'car',
  /** 車以外との事故 */
  object: 'object',
  /** 横転 */
  rollover: 'rollover',
  /** 落輪 */
  falling: 'falling',
} as const
export type AccidentStatusTypes = keyof typeof AccidentStatusTypes

/** 搬送先タイプ */
export const DestinationTypes = {
  /** 初期値 */
  '': '',
  /** ご存じのディーラー・修理工場等を予定 */
  specify: 'specify',
  /** スタッフ到着までに検討しておく */
  consider: 'consider',
  /** 入庫先紹介 */
  introduction: 'introduction',
} as const
export type DestinationTypes = keyof typeof DestinationTypes

/** エンジントラブル状況 */
export const EngineTroubleStatusTypes = {
  /** 初期値 */
  '': '',
  /** バッテリー上がり */
  battery: 'battery',
  /** エンジンがかからない */
  engineNotStart: 'engineNotStart',
  /** 走行中のエンスト */
  engineStop: 'engineStop',
  /** オ－バーヒート */
  overheat: 'overheat',
} as const
export type EngineTroubleStatusTypes = keyof typeof EngineTroubleStatusTypes

/** サポートタイプ */
export const TroubleSupportTypes = {
  /** 初期値 */
  '': '',
  /** 現場での応急対応 */
  emergencyMeasure: 'emergencyMeasure',
  /** レッカー搬送 */
  wrecker: 'wrecker',
} as const
export type TroubleSupportTypes = keyof typeof TroubleSupportTypes

/** 事故現場のタイプ */
export const FieldTypes = {
  '': '',
  gutter: 'gutter',
  curb: 'curb',
  others: 'others',
} as const
export type FieldTypes = keyof typeof FieldTypes

/** 車高状態のタイプ */
export const VehicleHeightStatusTypes = {
  /** 初期値 */
  '': '',
  /** 9cm以上 */
  moreThan9cm: 'moreThan9cm',
  /** 9cm未満 */
  under9cm: 'under9cm',
} as const
export type VehicleHeightStatusTypes = keyof typeof VehicleHeightStatusTypes

/** 周辺状況の種別 */
export const LocationTypes = {
  /** 初期値 */
  '': '',
  /** 一般道 */
  localLoad: 'localLoad',
  /** 高速道路 */
  highway: 'highway',
  /** 自宅・通常保管場所 */
  home: 'home',
  /** 上記以外の車庫・駐車場 */
  garageParking: 'garageParking',
  /** 舗装されていない道路 */
  noPavedLoad: 'noPavedLoad',
  /** その他 */
  other: 'other',
} as const
export type LocationTypes = keyof typeof LocationTypes

/** 高速道路の場所 */
export const HighwayLocationTypes = {
  /** 初期値 */
  '': '',
  /** 本線 */
  mainLine: 'mainLine',
  /** SA・PA */
  sapa: 'sapa',
} as const
export type HighwayLocationTypes = keyof typeof HighwayLocationTypes

/** 保管場所の特徴 */
export const StorageLocationTypes = {
  /** 屋根のない平地の駐車場 */
  streetParking: 'streetParking',
  /** 立体駐車場(機械式) */
  mechanical: 'mechanical',
  /** 立体駐車場(自走式) */
  selfDriving: 'selfDriving',
} as const
export type StorageLocationTypes = keyof typeof StorageLocationTypes

/** 油種 */
export const FuelTypes = {
  /** 初期値 */
  '': '',
  /** レギュラー */
  regular: 'regular',
  /** ハイオク */
  highOctane: 'highOctane',
  /** 軽油 */
  lightOil: 'lightOil',
  /** 電気・LPG・水素・その他特殊燃料 */
  electricity: 'electricity',
} as const
export type FuelTypes = keyof typeof FuelTypes

/** 落輪のトラブル状況 */
export const FallingWheelStatusTypes = {
  /** 初期値 */
  '': '',
  /** 落輪 */
  falling: 'falling',
  /** 乗り上げ */
  rideup: 'rideup',
} as const
export type FallingWheelStatusTypes = keyof typeof FallingWheelStatusTypes
/** スペアタイヤの状況 */
export const SpareTireStatusTypes = {
  /** 初期値 */
  '': '',
  /** スペアタイヤあり */
  yes: 'yes',
  /** なし */
  none: 'none',
  /** わからない */
  unknown: 'unknown',
} as const
export type SpareTireStatusTypes = keyof typeof SpareTireStatusTypes

/** パンクの原因 */
export const FlatTireCauseTypes = {
  /** 初期値 */
  '': '',
  /** 接触あり（何かを踏んだ等） */
  accident: 'accident',
  /** タイヤの劣化 */
  deterioration: 'deterioration',
  /** わからない */
  unknown: 'unknown',
} as const
export type FlatTireCauseTypes = keyof typeof FlatTireCauseTypes

/** トラブル発生日時 */
export const TroubleDatetimeTypes = {
  /** 初期値 */
  '': '',
  /** 先ほど・今しがた（今日） */
  todayNow: 'todayNow',
  /** 先ほど・今しがた */
  justNow: 'justNow',
  /** 今日の午前 */
  todayAM: 'todayAM',
  /** 昨日の午後 */
  yesterdayPM: 'yesterdayPM',
  /** 昨日の午前 */
  yesterdayAM: 'yesterdayAM',
  /** 上記以前 */
  earlier: {
    datetypes: 'earlier',
    date: '',
  },
} as const
export type TroubleDatetimeTypes = keyof typeof TroubleDatetimeTypes

/** トラブル発生日時の詳細 */
export type TroubleDetailDateTypes = {
  timing: TroubleDatetimeTypes
  date: string
}

/** カギのトラブル状況 */
export const LossKeyStatusTypes = {
  /** 初期値 */
  '': '',
  /** 車内にカギ閉じこみ */
  insideCar: 'insideCar',
  /** カギの紛失 */
  loss: 'loss',
} as const
export type LossKeyStatusTypes = keyof typeof LossKeyStatusTypes

/** 車内の状況 */
export const InsideVehicleStatusTypes = {
  /** 車内閉じこみ */
  confined: 'confined',
  /** エンジンがついたまま */
  engineIsOn: 'engineIsOn',
} as const
export type InsideVehicleStatusTypes = keyof typeof InsideVehicleStatusTypes

/** その他のトラブル状況 */
export const OthersStatusTypes = {
  /** 初期値 */
  '': '',
  /** 雪にはまった */
  snow: 'snow',
  /** オイル漏れ */
  oil: 'oil',
  /** オーバーヒート */
  overheat: 'overheat',
  /** 上記以外のトラブル */
  others: 'others',
} as const
export type OthersStatusTypes = keyof typeof OthersStatusTypes

/** その他のトラブル状況 (共済系ドメイン時) */
export const OthersKsStatusTypes = {
  /** 初期値 */
  '': '',
  /** 雪にはまった */
  snow: 'snow',
  /** 泥・砂にはまった */
  sand: 'sand',
  /** オイル漏れ */
  oil: 'oil',
  /** オーバーヒート */
  overheat: 'overheat',
  /** 上記以外のトラブル */
  others: 'others',
} as const
export type OthersKsStatusTypes = keyof typeof OthersKsStatusTypes

/** 雪・泥・砂にはまったトラブルの詳細 */
export const SnowDetailTypes = {
  '': '',
  snowWithNotNormal: 'snowWithNotNormal',
  snowWithNormal: 'snowWithNormal',
  mudAndSand: 'mudAndSand',
} as const
export type SnowDetailTypes = keyof typeof SnowDetailTypes

/** 続柄 */
export const RelationshipTypes = {
  /** 初期値 */
  '': '',
  /** 本人 */
  self: 'self',
  /** 家族 */
  family: 'family',
  /** 友人 */
  friend: 'friend',
  /** 従業員 */
  employee: 'employee',
  /** その他 */
  other: 'other',
} as const
export type RelationshipTypes = keyof typeof RelationshipTypes

/** 車両種別 */
export const VehicleTypes = {
  /** 初期値 */
  '': '',
  /** 軽/普通乗用車 */
  autoMobile: 'autoMobile',
  /** バイク */
  motorbike: 'motorbike',
  /** トラック */
  truck: 'truck',
  /** バス */
  bus: 'bus',
  /** その他 */
  other: 'other',
} as const
export type VehicleTypes = keyof typeof VehicleTypes

/** 排気量 */
export const DisplacementTypes = {
  /** 初期値 */
  '': '',
  /** ５０CC以下 */
  under50: 'under50',
  /** ５１～１２５CC */
  under125: 'under125',
  /** １２６～２５０CC */
  under250: 'under250',
  /** ２５１～４００CC */
  under400: 'under400',
  /** ４０１CC以上 */
  over401: 'over401',
} as const

export type DisplacementTypes = keyof typeof DisplacementTypes

/** 最大積載量 */
export const CargoTypes = {
  /** 初期値 */
  '': '',
  /** 軽トラック */
  lightTruck: 'lightTruck',
  /** 最大積載量２ｔ未満 */
  under2t: 'under2t',
  /** 最大積載量２ｔ以上４ｔ以下 */
  under4t: 'under4t',
  /** 最大積載量４ｔ超 */
  over4t: 'over4t',
  /** 最大積載量１０ｔ超 */
  over10t: 'over10t',
} as const
export type CargoTypes = keyof typeof CargoTypes

/** 搬送時の積み荷の量 */
export const CargoQuantityTypes = {
  /** 初期値 */
  '': '',
  /** 満載 */
  full: 'full',
  /** 5割以上 */
  over50per: 'over50per',
  /** 5割未満 */
  under50per: 'under50per',
  /** なし */
  none: 'none',
} as const
export type CargoQuantityTypes = keyof typeof CargoQuantityTypes

/**
 * QN用Stateの初期値
 */

const defaultInfo: VehicleStateInfo = {
  accidentImage: {
    peripheral: {
      image: '',
      orientation: 1,
    },
    damagedPart: {
      image: '',
      orientation: 1,
    },
    vehicleCloseUp: {
      image: '',
      orientation: 1,
    },
    landmark: {
      image: '',
      orientation: 1,
    },
    carVerification: {
      image: '',
      orientation: 1,
    },
    supplement: {
      image: '',
      orientation: 1,
    },
  },
  accidentPart: {
    parts: [],
    existsDamagedTire: false,
  },
  accidentStatus: AccidentStatusTypes[''],
  crossPrefectures: false,
  destinationType: DestinationTypes[''],
  engineTroubleStatus: EngineTroubleStatusTypes[''],
  spareTireStatus: SpareTireStatusTypes[''],
  flatTireCause: FlatTireCauseTypes[''],
  troubleSupport: TroubleSupportTypes[''],
  fieldType: {
    type: FieldTypes[''],
    others: '',
  },
  isMotorbike: false,
  isNotApplicableParts: false,
  isVehicleHeightModification: false,
  noPhoto: false,
  isAddPhoto: false,
  othersImage: {
    peripheral: {
      image: '',
      orientation: 1,
    },
    damagedPart: {
      image: '',
      orientation: 1,
    },
    vehicleCloseUp: {
      image: '',
      orientation: 1,
    },
    landmark: {
      image: '',
      orientation: 1,
    },
    carVerification: {
      image: '',
      orientation: 1,
    },
    supplement: {
      image: '',
      orientation: 1,
    },
  },
  fallingWheelStatus: FallingWheelStatusTypes[''],
  lossKeyStatus: LossKeyStatusTypes[''],
  insideVehicleStatus: [],
  modalFlag: false,
  fuelType: FuelTypes[''],
  othersStatus: OthersStatusTypes[''],
  othersKsStatus: OthersKsStatusTypes[''],
  snowDetail: SnowDetailTypes[''],
  othersDetail: '',
  vehicleHeightStatus: VehicleHeightStatusTypes[''],
}

export const initialQnEditState: QnEditStateProps = {
  contractInfo: {
    nameKana: '',
    lastName: '',
    firstName: '',

    phoneNumber: '',
    firstNumber: '',
    secondNumber: '',
    lastNumber: '',

    relationship: RelationshipTypes[''],
    contractorName: '',

    plateNumber: '',
    motorbikeAndOtherPlateNumber: '',
    region: '',
    classificationNumber: '',
    kana: '',
    designatedNumber: '',

    vehicleType: VehicleTypes[''],
    bikeDisplacement: DisplacementTypes[''],
    cargo: CargoTypes[''],
    cargoQuantity: CargoQuantityTypes[''],

    jafMembershipConfirmation: false,
    jafMembershipNumber: '',
    jafMembershipName: '',

    designatePartnerConfirmation: false,
    designatePartnerPhoneNumber: '',
    designatePartnerName: '',
  },
  locationStateInfo: {
    locationType: LocationTypes[''],
    highwayLocation: HighwayLocationTypes[''],
    homeStorageLocation: [],
    otherStorageLocation: [],
    localLoadSupplement: '',
    highwaySupplement: '',
    homeStorageSupplement: '',
    otherStorageSupplement: '',
    noPavedLoadSupplement: '',
    otherSupplement: '',
  },
  destinationLocation: {
    lat: 0,
    lng: 0,
    locationName: '',
    address: '',
    isSelected: false,
  },
  vehicleStateInfo: defaultInfo,
  reservation: {
    date: '',
    time: '',
  },
  troubleDatetime: {
    timing: '',
    date: '',
  },
}

export const QnState: QnStateProps = {
  errorCode: '',
  qnStatus: '',
  locationSharingTimeout: 90,
  confirmationChangeType: '',
  sendError: false,
  vehicleState: '',
  vehicleStateInfo: defaultInfo,
  contractInfo: initialQnEditState.contractInfo,
  destinationLocation: {
    lat: 0,
    lng: 0,
    locationName: '',
    address: '',
    isSelected: false,
  },
  locationStateInfo: initialQnEditState.locationStateInfo,
  location: {
    lat: 0,
    lng: 0,
    locationName: '',
    address: '',
    isSelected: false,
  },
  partner: {
    lat: 0,
    lng: 0,
    visible: false,
  },
  partnerStable: true,
  partnerStableQueue: [true, true, true, true, true],
  partnerName: '',
  arrivalDuration: 0,
  siteArrivalScheduleDate: '',
  timeComingSoon: -1,
  lastLocationSendDate: '',
  raTransferScWishes: {
    title: VEHICLE_STATE_CONFIRM_TITLE.RA_TRANSFER_SC_WISHES,
    formName: VEHICLE_STATE_CONFIRM_TITLE.RA_TRANSFER_SC_WISHES,
    isRequired: true,
    isExclusion: true,
    checks: [
      {
        label: RA_TRANSFER_SC_WISHES.YES,
        isChecked: false,
      },
      {
        label: RA_TRANSFER_SC_WISHES.NO,
        isChecked: false,
      },
      {
        label: RA_TRANSFER_SC_WISHES.THINKING,
        isChecked: false,
      },
    ],
  },
  raLodgingScWishes: {
    title: VEHICLE_STATE_CONFIRM_TITLE.RA_LODGING_SC_WISHES,
    formName: VEHICLE_STATE_CONFIRM_TITLE.RA_LODGING_SC_WISHES,
    isRequired: true,
    isExclusion: true,
    checks: [
      {
        label: RA_LODGING_SC_WISHES.YES,
        isChecked: false,
      },
      {
        label: RA_LODGING_SC_WISHES.NO,
        isChecked: false,
      },
      {
        label: RA_LODGING_SC_WISHES.THINKING,
        isChecked: false,
      },
    ],
  },
  raLcarCostScWishes: {
    title: VEHICLE_STATE_CONFIRM_TITLE.RA_LCAR_COST_SC_WISHES,
    formName: VEHICLE_STATE_CONFIRM_TITLE.RA_LCAR_COST_SC_WISHES,
    description: RA_LCAR_COST_SC_WISHES.DESCRIPTION,
    isRequired: true,
    isExclusion: true,
    checks: [
      {
        label: RA_LCAR_COST_SC_WISHES.YES,
        isChecked: false,
      },
      {
        label: RA_LCAR_COST_SC_WISHES.NO,
        isChecked: false,
      },
      {
        label: RA_LCAR_COST_SC_WISHES.ALREADY_ARRANGED,
        isChecked: false,
      },
    ],
  },
  raLcarCostScSupplWishes: {
    title: `${VEHICLE_STATE_CONFIRM_TITLE.RA_LCAR_COST_SC_SUPPL_WISHES}を入力（60文字まで入力可能）`,
    formName: VEHICLE_STATE_CONFIRM_TITLE.RA_LCAR_COST_SC_SUPPL_WISHES,
    isRequired: false,
    text: {
      inputText: '',
      placeholder: '例１：禁煙希望します。\n例２：チャイルドシートが必要です。',
      maxLength: 60,
    },
  },
  confirmationAgree: YesNoSelection.no,
  productKbnCd: '',
  raLodgingTransfer: '',
  raLcarCost: '',
  reservation: {
    date: '',
    time: '',
  },
  currentInputPagePath: '',
  urlParameters: {
    client: '',
    from: '',
    line: '',
  },
  initialContractInfo: undefined,
  troubleDatetime: {
    timing: initialQnEditState.troubleDatetime.timing,
    date: '',
  },
  version: QnFrontVersion,
}

/** 画像情報 */
export type ImageInfo = {
  /** イメージパス */
  image: string
  /** 回転設定 */
  orientation: number
}

export type AccidentImage = {
  /** 周辺状況を含めた画像 */
  peripheral: ImageInfo
  /** 破損個所の画像 */
  damagedPart: ImageInfo
  /** 車両近接画像 */
  vehicleCloseUp: ImageInfo
  /** 付近の目印などの画像 */
  landmark: ImageInfo
  /** 車検証などの画像 */
  carVerification: ImageInfo
  /** 上記以外の画像 */
  supplement: ImageInfo
}
export type AccidentImageKeys = keyof AccidentImage

/** 破損個所情報 */
export type AccidentPartInfo = {
  /** 破損個所の一覧 */
  parts: VehiclePart[]
  /** 破損タイヤの有無 */
  existsDamagedTire: boolean
}

/** 事故現場情報 */
export type FieldTypeInfo = {
  type: FieldTypes
  others: string
}

/** 車の状況情報 */
export type VehicleStateInfo = {
  /** 画像情報 */
  accidentImage: AccidentImage
  /** 破損個所情報 */
  accidentPart: AccidentPartInfo
  /** 事故状況 */
  accidentStatus: AccidentStatusTypes
  /** 都道府県外への移動 */
  crossPrefectures: boolean
  /** 自走できない場合の運び先 */
  destinationType: DestinationTypes
  /** エンジントラブルの状況 */
  engineTroubleStatus: EngineTroubleStatusTypes
  /** 事故現場 */
  fieldType: FieldTypeInfo
  /** バイクかどうか */
  isMotorbike: boolean
  /** 四輪・バイク以外でタイヤ軸が複数ある車かどうか */
  isNotApplicableParts: boolean
  /** 車高改造の有無 */
  isVehicleHeightModification: boolean
  /** 写真以外 */
  noPhoto: boolean
  /** 写真の追加 */
  isAddPhoto: boolean
  /** その他の現場写真 */
  othersImage: AccidentImage
  /** カギのトラブル状況 */
  lossKeyStatus: LossKeyStatusTypes
  /** 車内の状況 */
  insideVehicleStatus: InsideVehicleStatusTypes[]
  /** 架電モーダルフラグ */
  modalFlag: boolean
  /** 油種 */
  fuelType: FuelTypes
  /** 落輪のトラブル状況 */
  fallingWheelStatus: FallingWheelStatusTypes
  /** スペアタイヤの状況 */
  spareTireStatus: SpareTireStatusTypes
  /** パンクの原因 */
  flatTireCause: FlatTireCauseTypes
  /** トラブルの対応希望 */
  troubleSupport: TroubleSupportTypes
  /** 車高タイプ */
  vehicleHeightStatus: VehicleHeightStatusTypes
  /** その他のトラブル状況 */
  othersStatus: OthersStatusTypes
  /** その他のトラブル状況 (共済系ドメイン時) */
  othersKsStatus: OthersKsStatusTypes
  /** 雪・泥・砂にはまったトラブルの詳細 */
  snowDetail: SnowDetailTypes
  /** 雪・泥・砂にはまったトラブルの詳細 */
  othersDetail: string
}

/** 契約情報 */
export type ContractInfo = {
  /** 名前(カナ) */
  nameKana: string
  /** 苗字 */
  lastName: string
  /** 名前 */
  firstName: string
  /** 電話番号 */
  phoneNumber: string
  firstNumber: string
  secondNumber: string
  lastNumber: string
  /** 契約者との関係 */
  relationship: RelationshipTypes
  /** 契約者名（本人以外の場合） */
  contractorName: string
  /** ナンバープレート情報 */
  plateNumber: string
  /** バイク・その他 */
  motorbikeAndOtherPlateNumber: string
  /** 地域名 */
  region: string
  /** 分類番号 */
  classificationNumber: string
  /** ひらがな */
  kana: string
  /** 一連指定番号 */
  designatedNumber: string
  /** 車両種別 */
  vehicleType: VehicleTypes
  /** 排気量 */
  bikeDisplacement: DisplacementTypes
  /** 最大積載量 */
  cargo: CargoTypes
  /** 搬送時の積み荷の量 */
  cargoQuantity: CargoQuantityTypes
  /** JAFへの加入の有無 */
  jafMembershipConfirmation: boolean
  /** JAF会員番号 */
  jafMembershipNumber: string
  /** JAF会員名 */
  jafMembershipName: string
  /** お客様指定業者 */
  designatePartnerConfirmation: boolean
  /** 指定業者の電話番号 */
  designatePartnerPhoneNumber: string
  /** 指定業者名 */
  designatePartnerName: string
}

/** 予約情報 */
export type ReservationProps = {
  /** 予約日(YYYY/MM/DD) */
  date: string
  /** 予約時間(HH:mm) */
  time: string
}

/** 周辺状況 */
export type LocationStateInfo = {
  /** 場所の種別 */
  locationType: LocationTypes
  /** 高速道路の場所 */
  highwayLocation: HighwayLocationTypes
  /** 自宅・通常保管場所の特徴 */
  homeStorageLocation: StorageLocationTypes[]
  /** 他の保管場所の特徴 */
  otherStorageLocation: StorageLocationTypes[]
  /** 一般道の補足情報 */
  localLoadSupplement: string
  /** 高速道路の補足情報 */
  highwaySupplement: string
  /** 自宅・通常保管場所の補足情報 */
  homeStorageSupplement: string
  /** 他の保管場所の補足情報 */
  otherStorageSupplement: string
  /** 舗装されていない道路の補足情報 */
  noPavedLoadSupplement: string
  /** その他の補足情報 */
  otherSupplement: string
}

/**
 * 編集用のStateデータ
 */
export type QnEditStateProps = {
  /** 契約情報 */
  contractInfo: ContractInfo
  /** 搬送先情報 */
  destinationLocation: LocationConfirmStateProps
  /** 周辺状況 */
  locationStateInfo: LocationStateInfo
  /** 予約情報 */
  reservation: ReservationProps
  /** トラブル発生日時 */
  troubleDatetime: TroubleDetailDateTypes
  /** 車の状況 */
  vehicleStateInfo: VehicleStateInfo
  /** トラブルの種類 */
  vehicleState?: string
}

export type URLParameters = {
  client: string
  from: string
  line: string
  params?: string
}

/**
 * 型定義：QN用State
 */
export type QnStateProps = QnFrontVersionProps & {
  errorCode: string
  qnStatus: string
  locationSharingTimeout: number
  confirmationChangeType: string
  sendError: boolean
  vehicleState: string
  vehicleStateInfo: VehicleStateInfo
  destinationLocation: LocationConfirmStateProps
  location: LocationConfirmStateProps
  partner: TrackingProps
  partnerStable: boolean
  partnerStableQueue: Array<boolean>
  partnerName: string
  arrivalDuration: number
  siteArrivalScheduleDate: string
  timeComingSoon: number
  lastLocationSendDate: string
  raTransferScWishes: CheckGroupProps
  raLodgingScWishes: CheckGroupProps
  raLcarCostScWishes: CheckGroupProps
  raLcarCostScSupplWishes: TextAreaGroupProps
  confirmationAgree: YesNoSelection
  productKbnCd: string
  raLodgingTransfer: string
  raLcarCost: string
  reservation: ReservationProps
  currentInputPagePath: string
  contractInfo: ContractInfo
  troubleDatetime: TroubleDetailDateTypes
  locationStateInfo: LocationStateInfo
  urlParameters: URLParameters
  initialContractInfo?: ContractInfo
}

/**
 * 型定義：QN用StateのHooks
 */
export type QnStatesProps = {
  state: QnStateProps
  setState: (sampleState: QnStateProps) => StatusProps
}
